/**
 * Veri yaşam döngüsü ekranlarının ortak parçaları (M6 WP 6.4): yeniden kimlik doğrulama alanları, sayı tabloları, hata metni.
 */
import {
  RETAINED_REASONS,
  summarizeCounts,
  type EraseOutcome,
  type SubjectRequestStatus,
} from '@castinflow/shared';
import type { ReactNode } from 'react';
import { Alert, Badge, Field, Input, PageHeader, type BadgeTone } from '@castinflow/ui';
import { ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { usePageMeta } from '../../shell/page-meta';
import { tr } from '../../i18n/tr';
import { lifecycleText as T } from './lifecycle-strings';
import { formatDateTimeTr } from '../../lib/dates';

export const formatDateTime = (iso: string): string => formatDateTimeTr(iso);

/**
 * Yönetici kapısı (F3 · TI-067/TI-068/TI-069): `settings.manage` yoksa sayfa gövdesi HİÇ çizilmez (form, CSV düğmesi, uç çağrısı
 * yok) ve yetki sınırı açıkça söylenir. Sunucu da reddeder; bu kapı yalnız yanıltıcı "yüklenemedi" hatasını ve işe yaramayan
 * formu kaldırır.
 */
export function AdminOnly({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  const canManage = useSession((s) => s.me?.capabilities.includes('settings.manage') ?? false);
  if (canManage) return <>{children}</>;
  return <AdminOnlyDenied title={title} description={description} testId={testId} />;
}

function AdminOnlyDenied({
  title,
  description,
  testId,
}: {
  title: string;
  description?: ReactNode;
  testId: string;
}) {
  usePageMeta(title, [{ label: tr.nav.administration }]);
  return (
    <div className="space-y-4" data-testid={testId} data-access="denied">
      <PageHeader title={title} description={description} />
      <div data-testid="admin-only">
        <Alert tone="info" title={T.adminOnly} />
      </div>
    </div>
  );
}

const NUM1 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 });
export function formatSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = Math.max(0, bytes);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${NUM1.format(v)} ${units[i] ?? 'B'}`;
}

/** Sunucu hatasını kullanıcı metnine çevirir (teknik ileti gösterilmez) */
export function lifecycleErrorText(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback;
  if (e.code === 'RATE_LIMITED') return T.rateLimited;
  if (e.code === 'UNAUTHENTICATED') return T.reauthFailed;
  if (e.code === 'FORBIDDEN') return T.forbidden;
  if (e.code === 'VALIDATION_FAILED') return T.confirmMismatch;
  // 409: sunucunun açıklayıcı Türkçe iletisi (durum çakışması)
  if (e.code === 'CONFLICT') return e.message;
  return fallback;
}

export const STATUS_TONE: Record<SubjectRequestStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  ready: 'primary',
  confirmed: 'info',
  completed: 'success',
  failed: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
};

export function StatusBadge({ status }: { status: SubjectRequestStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{T.statuses[status]}</Badge>;
}

/** Parola + (varsa) doğrulama kodu. Değerler yalnız çağıran bileşenin yerel durumundadır. */
export function ReauthFields({
  password,
  totpCode,
  onPassword,
  onTotp,
  idPrefix,
  passwordHint = T.passwordHint,
}: {
  password: string;
  totpCode: string;
  onPassword: (v: string) => void;
  onTotp: (v: string) => void;
  idPrefix: string;
  /** Parola alanının açıklaması (varsayılan: geri alınamaz işlem; K1 yönetici daveti kendi metnini verir) */
  passwordHint?: string;
}) {
  return (
    <>
      <Field label={T.password} description={passwordHint} required>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            onPassword(e.target.value);
          }}
          data-testid={`${idPrefix}-password`}
        />
      </Field>
      <Field label={T.totp} description={T.totpHint} optional>
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={totpCode}
          onChange={(e) => {
            onTotp(e.target.value.replace(/\D/g, ''));
          }}
          data-testid={`${idPrefix}-totp`}
        />
      </Field>
    </>
  );
}

function CountList({
  title,
  counts,
  withReasons = false,
  testId,
}: {
  title: string;
  counts: Readonly<Record<string, number>>;
  withReasons?: boolean;
  testId: string;
}) {
  const rows = summarizeCounts(counts);
  return (
    <section className="space-y-1.5" data-testid={testId}>
      <h4 className="text-base font-semibold text-fg">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-fg-muted">{T.subject.none}</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-surface">
          {rows.map((r) => {
            const reason = withReasons
              ? r.keys.map((k) => RETAINED_REASONS[k]).find((x) => x !== undefined)
              : undefined;
            return (
              <li key={r.label} className="px-3 py-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-fg">{r.label}</span>
                  <span
                    className="tabular-nums text-fg"
                    data-testid={`${testId}-${r.keys[0] ?? ''}`}
                  >
                    {T.subject.count(r.count)}
                  </span>
                </div>
                {reason === undefined ? null : <p className="text-sm text-fg-muted">{reason}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Ön izleme / sonuç: silinen, anonimleştirilen, korunan — sayılarla ve korunma gerekçesiyle */
export function OutcomeSummary({
  outcome,
  labels,
  testId,
}: {
  outcome: EraseOutcome;
  labels: { deleted: string; anonymized: string; retained: string };
  testId: string;
}) {
  return (
    <div className="space-y-4" data-testid={testId}>
      <CountList title={labels.deleted} counts={outcome.deleted} testId={`${testId}-deleted`} />
      <CountList
        title={labels.anonymized}
        counts={outcome.anonymized}
        testId={`${testId}-anonymized`}
      />
      <CountList
        title={labels.retained}
        counts={outcome.retained}
        withReasons
        testId={`${testId}-retained`}
      />
      <p className="text-sm text-fg-muted">
        {T.subject.objects(outcome.objects)} · {T.subject.queueJobs(outcome.queueJobs)}
      </p>
    </div>
  );
}
